import React, { useState, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Alert,
  message,
  Button,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  UserAddOutlined,
  MailOutlined,
  LockOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { useAuth } from "./AuthContext";

const { Text, Title } = Typography;

const RegisterModal = () => {
  const {
    user,
    register,
    loginModalVisible,
    onCloseLoginModal,
    registerModalVisible,
    onCloseRegisterModal,
    onOpenLoginModal,
    onOpenOtpModal,
  } = useAuth();
  const formRef = useRef(null);
  const [registering, setRegistering] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // const [registerModalVisible, setRegisterModalVisible] = useState(false);

  const handleCancel = () => {
    // @ts-ignore
    formRef.current?.resetFields();
    setErrorMessage("");
    onCloseRegisterModal();
  };

  const onFinish = async (values: any) => {
    setRegistering(true);
    setErrorMessage("");

    try {
      // Prepare registration parameters
      const params = {
        first_name: values.firstName,
        email: values.email,
        password: values.password,
        phone_number: values.phone,
      };

      console.log("Registration params:", params);

      const response = await register(params);

      // Reset form and close modal
      // @ts-ignore
      formRef.current?.resetFields();
      onCloseRegisterModal();

      onOpenOtpModal(response.UUID, values.email);

      message.info("Please check your email for verification code.");

      console.log("Registration response:", response);
    } catch (err: any) {
      const errorMsg =
        err?.error_message ||
        err?.message ||
        "Registration failed. Please try again.";
      setErrorMessage(errorMsg);
      message.error(errorMsg);
    } finally {
      setRegistering(false);
    }
  };

  const switchToLogin = () => {
    handleCancel();
    onOpenLoginModal();
  };

  return (
    <>
      {/* @ts-ignore */}
      <Modal
        title={
          <Space
            direction="vertical"
            align="center"
            style={{ width: "100%", textAlign: "center" }}
          >
            <UserAddOutlined
              style={{ fontSize: "24px", color: "#1890ff" }}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <Title level={4} style={{ margin: 0 }}>
              Create Account
            </Title>
          </Space>
        }
        visible={registerModalVisible}
        footer={null}
        onCancel={handleCancel}
        destroyOnClose
        width={400}
        centered
      >
        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            style={{ marginBottom: "1em" }}
          />
        )}

        <Form
          ref={formRef}
          name="register-form"
          layout="vertical"
          autoComplete="off"
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            label="Full Name"
            name="firstName"
            rules={[
              { required: true, message: "Please enter your full name" },
              { min: 2, message: "Name must be at least 2 characters" },
            ]}
          >
            <Input
              prefix={
                <UserAddOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              placeholder="Enter your full name"
              autoComplete="name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone"
            rules={[
              { required: true, message: "Please enter your phone number" },
              {
                pattern: /^[0-9+\-\s()]+$/,
                message: "Please enter a valid phone number",
              },
            ]}
          >
            <Input
              prefix={
                <PhoneOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              placeholder="Enter your phone number"
              autoComplete="tel"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={
                <MailOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              placeholder="Enter your email"
              autoComplete="email"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password
              prefix={
                <LockOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              placeholder="Create a password"
              autoComplete="new-password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={
                <LockOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              placeholder="Confirm your password"
              autoComplete="new-password"
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: "16px" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={registering}
              block
              size="large"
            >
              {registering ? "Creating Account..." : "Create Account"}
            </Button>
          </Form.Item>
        </Form>

        <Divider plain>Or</Divider>

        <div style={{ textAlign: "center" }}>
          <Text type="secondary">Already have an account? </Text>
          <Button
            type="link"
            onClick={switchToLogin}
            style={{ padding: 0, height: "auto" }}
          >
            Sign In
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default RegisterModal;
